import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  getDietTemplates, getTrainingTemplates,
  saveDietTemplate, saveTrainingTemplate,
  deleteDietTemplate, deleteTrainingTemplate,
} from "@/lib/planTemplates";
import {
  UtensilsCrossed, Dumbbell, Plus, Trash2, Copy, FileStack, Loader2,
} from "lucide-react";
import DisciplineBadge from "@/components/training/DisciplineBadge";
import { formatExercisePrescription, getDiscipline } from "@/lib/trainingDisciplines";

function countStrengthExercises(plan) {
  return (plan?.days || []).reduce(
    (sum, day) => sum + (day.exercises || []).filter((ex) => ex.lift_key || ex.intensity_pct).length,
    0
  );
}

function planExercisePreview(plan) {
  const discipline = plan?.discipline || "strength";
  const samples = (plan?.days || [])
    .flatMap((d) => (d.exercises || []).slice(0, 2))
    .slice(0, 3)
    .map((ex) => {
      const rx = formatExercisePrescription(ex, discipline, {});
      return rx ? `${ex.name || "?"} (${rx})` : ex.name;
    })
    .filter(Boolean);
  return samples.join(" · ");
}

export default function PlanTemplates() {
  const navigate = useNavigate();
  const [dietTemplates, setDietTemplates] = useState([]);
  const [trainingTemplates, setTrainingTemplates] = useState([]);
  const [dietPlans, setDietPlans] = useState([]);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [saveType, setSaveType] = useState("diet");
  const [applyTemplate, setApplyTemplate] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [applyStudentId, setApplyStudentId] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = () => {
    setDietTemplates(getDietTemplates());
    setTrainingTemplates(getTrainingTemplates());
  };

  useEffect(() => {
    refresh();
    api.get("/diet-plans").then((r) => setDietPlans(r.data)).catch(() => {});
    api.get("/training-plans").then((r) => setTrainingPlans(r.data)).catch(() => {});
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
  }, []);

  const openSave = (type) => {
    setSaveType(type);
    setTemplateName("");
    setSelectedPlanId("");
    setSaveOpen(true);
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
      toast.error("Donnez un nom au modèle");
      return;
    }
    if (!selectedPlanId) {
      toast.error("Sélectionnez un plan source");
      return;
    }

    if (saveType === "diet") {
      const plan = dietPlans.find((p) => p.plan_id === selectedPlanId);
      if (!plan) return;
      saveDietTemplate({ name: templateName.trim(), data: plan });
      toast.success("Modèle diet enregistré");
    } else {
      const plan = trainingPlans.find((p) => p.plan_id === selectedPlanId);
      if (!plan) return;
      saveTrainingTemplate({ name: templateName.trim(), data: plan });
      toast.success("Modèle training enregistré");
    }
    refresh();
    setSaveOpen(false);
  };

  const handleDelete = (type, id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    if (type === "diet") deleteDietTemplate(id);
    else deleteTrainingTemplate(id);
    refresh();
    toast.success("Modèle supprimé");
  };

  const openApply = (template) => {
    setApplyTemplate(template);
    setApplyStudentId("");
    setApplyOpen(true);
  };

  const handleApply = async () => {
    if (!applyTemplate || !applyStudentId) {
      toast.error("Sélectionnez un élève");
      return;
    }
    setLoading(true);
    try {
      const { data: template } = applyTemplate;
      if (applyTemplate.type === "diet") {
        await api.post("/diet-plans", {
          student_id: applyStudentId,
          title: data.title || applyTemplate.name,
          description: data.description || "",
          meals: data.meals || [],
          calories_target: data.calories_target,
          protein_target: data.protein_target,
          carbs_target: data.carbs_target,
          fat_target: data.fat_target,
          water_target: data.water_target,
          steps_target: data.steps_target,
        });
        toast.success("Plan diet créé depuis le modèle");
        navigate("/coach/diet-plans");
      } else {
        await api.post("/training-plans", {
          student_id: applyStudentId,
          title: data.title || applyTemplate.name,
          description: data.description || "",
          discipline: data.discipline || "strength",
          days: data.days || [],
        });
        toast.success("Plan training créé depuis le modèle");
        navigate("/coach/training-plans");
      }
      setApplyOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'application");
    } finally {
      setLoading(false);
    }
  };

  const TemplateList = ({ templates, type }) => (
    templates.length === 0 ? (
      <div className="text-center py-12">
        <FileStack className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-400">Aucun modèle enregistré</p>
        <p className="text-xs text-slate-400 mt-1">Sauvegardez un plan existant comme modèle réutilisable</p>
      </div>
    ) : (
      <div className="space-y-3">
        {templates.map((t) => (
          <Card key={t.id} className="border-slate-100" data-testid={`template-${t.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-900 truncate">{t.name}</h3>
                <p className="text-sm text-slate-500 truncate mt-0.5">{t.data?.title || "Sans titre"}</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {type === "training" && t.data?.discipline && (
                    <DisciplineBadge discipline={t.data.discipline} size="sm" />
                  )}
                  <Badge variant="secondary" className="text-[10px]">
                    {type === "diet"
                      ? `${t.data?.meals?.length || 0} repas`
                      : `${t.data?.days?.length || 0} jours`}
                  </Badge>
                  {type === "training" && countStrengthExercises(t.data) > 0 && (
                    <Badge variant="outline" className="text-[10px] border-orange-200 text-orange-700">
                      {countStrengthExercises(t.data)} exo. force
                    </Badge>
                  )}
                  <span className="text-[10px] text-slate-400">
                    {new Date(t.created_at).toLocaleDateString("fr-FR")}
                  </span>
                </div>
                {type === "training" && t.data?.days?.length > 0 && planExercisePreview(t.data) && (
                  <p className="text-[10px] text-slate-400 mt-1.5 truncate">
                    {planExercisePreview(t.data)}
                  </p>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openApply({ ...t, type })}
                  className="rounded-full gap-1 text-xs"
                >
                  <Copy className="w-3.5 h-3.5" /> Appliquer
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(type, t.id)}
                  className="h-8 w-8 text-red-400 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  );

  return (
    <div data-testid="plan-templates-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Modèles de plans</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Sauvegardez et réutilisez vos plans diet & training (stockage local)
          </p>
        </div>
      </div>

      <Tabs defaultValue="diet" className="mt-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="diet" className="gap-1.5">
            <UtensilsCrossed className="w-4 h-4" /> Diet ({dietTemplates.length})
          </TabsTrigger>
          <TabsTrigger value="training" className="gap-1.5">
            <Dumbbell className="w-4 h-4" /> Training ({trainingTemplates.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="diet" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => openSave("diet")} className="bg-green-600 hover:bg-green-700 text-white rounded-full gap-2">
              <Plus className="w-4 h-4" /> Nouveau modèle diet
            </Button>
          </div>
          <TemplateList templates={dietTemplates} type="diet" />
        </TabsContent>

        <TabsContent value="training" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => openSave("training")} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full gap-2">
              <Plus className="w-4 h-4" /> Nouveau modèle training
            </Button>
          </div>
          <TemplateList templates={trainingTemplates} type="training" />
        </TabsContent>
      </Tabs>

      {/* Save template dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Sauvegarder un modèle {saveType === "diet" ? "diet" : "training"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Nom du modèle</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Ex: Plan sèche 2000 kcal"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label>Plan source</Label>
              <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                <SelectTrigger className="mt-1.5 h-11">
                  <SelectValue placeholder="Sélectionner un plan existant" />
                </SelectTrigger>
                <SelectContent>
                  {(saveType === "diet" ? dietPlans : trainingPlans).map((p) => (
                    <SelectItem key={p.plan_id} value={p.plan_id}>
                      {saveType === "training" && p.discipline
                        ? `[${getDiscipline(p.discipline).shortLabel}] `
                        : ""}
                      {p.title} — {students.find((s) => s.user_id === p.student_id)?.name || "Élève"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSaveTemplate} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11">
              Enregistrer le modèle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Apply template dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Appliquer « {applyTemplate?.name} »
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Assigner à l'élève</Label>
              <Select value={applyStudentId} onValueChange={setApplyStudentId}>
                <SelectTrigger className="mt-1.5 h-11">
                  <SelectValue placeholder="Sélectionner un élève" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleApply}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11 gap-2"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Création...</> : "Créer le plan"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}